import { SB } from '@play-co/replicant';
import createScheduledActions, {
  extractScheduledActionHandlers,
} from '../config/scheduledActions/createScheduledActions';
import { generateReplicantChatbotPayload } from '../chatbot/messageTemplates';
import { Feature, sendAnalyticsEvents } from '../analytics';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import { getLocale } from '../getters/user';
import { isOnlineOrIdle } from '../getters/presence';
import { DaycareId, daycareOptions } from './daycare.ruleset';
import { getChatbotMessageChannel } from '../chatbot/chatbot.getters';
import { deliverChatbotMessage } from '../chatbot/chatbot.util';
import * as Pet from '../pet';

const schema = {
  daycareFinished: SB.object({
    // We use loose schema validation for greater backwards compatibility but
    // cast as a tuple schema for strong compile-time checking against current ruleset
    type: SB.string() as unknown as SB.TupleSchema<DaycareId>,
  }),
};

export default {
  schema,
  actions: extractScheduledActionHandlers(
    createScheduledActions(schema)({
      daycareFinished: (state, { type }, api) => {
        sendAnalyticsEvents(api, [
          {
            eventType: 'DaycareCheckOut',
            eventProperties: {
              item_id: type,
              earlyCheckout: false,
              minutesInDaycare: daycareOptions[type]?.duration,
            },
          },
        ]);

        const playerIsOnlineOrIdle = isOnlineOrIdle(api);

        // Force ES re-indexing
        if (!playerIsOnlineOrIdle) {
          api.postMessage.offlineUpdateState(state.id, {});
        }

        // Don't sent chatbot message if player is online or idle, or has muted notifications
        const channel = getChatbotMessageChannel(
          state,
          { name: 'daycare' },
          api.date.now(),
        );
        if (
          playerIsOnlineOrIdle ||
          state.settings.receiveMessages === 'off' ||
          !channel
        ) {
          return;
        }

        const {
          getEquippedPetSpecies,
          getAnalyticsUserProperties,
          getAnalyticsAttributeUserProperties,
          getPetName,
        } = Pet.getters;

        const payload = generateReplicantChatbotPayload({
          feature: Feature.DAYCARE._,
          $subFeature: getDaycareSubfeature(type),
        });

        const now = api.date.now();
        const message = getChatbotFlexMessage({
          template: 'daycare',
          payload,
          data: {
            locale: getLocale(state),
            species: getEquippedPetSpecies(state)!,
            petName: getPetName(state)!,
          },
          analyticsUserProperties: {
            ...getAnalyticsUserProperties(state, now),
            ...getAnalyticsAttributeUserProperties(state, now),
          },
        });

        const receiverId = state.id;
        deliverChatbotMessage(state, api, {
          receiverId,
          message,
          channel,
        });
      },
    }),
  ),
};

function getDaycareSubfeature(daycareId: DaycareId) {
  switch (daycareId) {
    case 'd_daycare_6h':
      return Feature.DAYCARE.SIX_HOUR;
    default:
      return getDefaultDaycareSubfeature(daycareId);
  }
}

// Provides exhaustiveness check with a default value instead of
// throwing an error so we can maintain backwards compatibility
function getDefaultDaycareSubfeature(daycareId: never) {
  return `${Feature.DAYCARE._}_${daycareId}`;
}
